import { Link } from 'react-router-dom';
import { useCompany } from '../lib/company';
import { Mail, Phone, MapPin, MessageCircle, Store, ShieldCheck, Building2, Landmark, IndianRupee, ChevronRight, Clock, ScrollText } from 'lucide-react';

export default function Contact() {
  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-10">
      {/* Breadcrumbs */}
      <nav className="text-xs text-ink/50 mb-4 inline-flex items-center gap-1" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-[#D35400]">Home</Link>
        <ChevronRight className="w-3 h-3"/>
        <span className="text-ink/70 font-semibold">Contact us</span>
      </nav>

      {/* Hero */}
      <div className="rounded-2xl bg-gradient-to-r from-[#0D47A1] to-[#1565C0] text-white p-6 md:p-10">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-white/70">We're here — every day</div>
        <h1 className="font-heading font-black text-3xl md:text-5xl mt-1">Talk to BharatCart</h1>
        <p className="text-white/80 mt-2 max-w-xl">Buyer, shopkeeper, delivery partner, journalist or curious neighbour — pick a channel and we'll respond quickly. Most queries are answered in under 2 hours during work hours.</p>
      </div>

      {/* Quick contact cards */}
      <section className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <ContactCard
          testId="contact-phone"
          Icon={Phone}
          title="Call us"
          value={<a href="tel:+911800000000" className="hover:underline">+91 1800-000-000</a>}
          hint="Toll-free · 10 AM – 8 PM IST · Mon–Sat"
          tone="green"
        />
        <ContactCard
          testId="contact-whatsapp"
          Icon={MessageCircle}
          title="WhatsApp"
          value={<a href="https://wa.me/911800000000" target="_blank" rel="noreferrer" className="hover:underline">+91 1800-000-000</a>}
          hint="24×7 · Order status · Refunds · Complaints"
          tone="green"
        />
        <ContactCard
          testId="contact-email"
          Icon={Mail}
          title="Email"
          value={<a href="mailto:support@bharatcart.org" className="hover:underline">support@bharatcart.org</a>}
          hint="Reply within 1 business day"
          tone="blue"
        />
      </section>

      {/* Specialised channels */}
      <section className="mt-8">
        <h2 className="font-heading font-bold text-2xl">Specialised channels</h2>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <SpecCard Icon={Store} title="Become a BharatCart seller"
            email="onboarding@bharatcart.org" phone="+91 9999-000-000" hint="Kirana, dairy, medical, apparel — join in ≤48 hours"/>
          <SpecCard Icon={IndianRupee} title="Payments & refunds"
            email="refunds@bharatcart.org" phone="+91 1800-000-000" hint="Order ID + Razorpay reference help fast-track"/>
          <SpecCard Icon={ShieldCheck} title="Grievance Officer (IT Act §5-9)"
            email="grievance@bharatcart.org" phone="+91 1800-000-000" hint="Response within 30 days as required by law"/>
          <SpecCard Icon={ScrollText} title="Data Protection Officer (DPDP Act)"
            email="dpo@bharatcart.org" phone="+91 1800-000-000" hint="Data access, correction, erasure requests"/>
        </div>
      </section>

      {/* Registered office */}
      <section className="mt-10 grid md:grid-cols-5 gap-6 items-start">
        <div className="md:col-span-3">
          <h2 className="font-heading font-bold text-2xl inline-flex items-center gap-2"><Building2 className="w-6 h-6 text-[#0D47A1]"/>Registered office</h2>
          <div className="mt-4 card-soft p-5">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[#D35400] flex-shrink-0 mt-0.5"/>
              <div>
                <div className="font-heading font-bold text-lg">BharatCart Pvt. Ltd.</div>
                <div className="text-sm text-ink/70 mt-1 leading-relaxed">
                  4th Floor, BharatCart Tower<br/>
                  Kondapur, Hyderabad<br/>
                  Telangana 500 084, India
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-border text-xs text-ink/60 space-y-1">
              <div><b>CIN:</b> U74999TG2026PTC000000 (mock)</div>
              <CompanyIds/>
              <div><b>PAN:</b> AAKCMXXXXK</div>
            </div>
          </div>
        </div>
        <div className="md:col-span-2">
          <h2 className="font-heading font-bold text-2xl inline-flex items-center gap-2"><Clock className="w-6 h-6 text-[#0D47A1]"/>Support hours</h2>
          <div className="mt-4 card-soft p-5 text-sm space-y-2">
            <Row label="Phone / Chat" value="10 AM – 8 PM · Mon–Sat"/>
            <Row label="WhatsApp bot" value="24 × 7"/>
            <Row label="Email" value="Response within 1 business day"/>
            <Row label="Legal / DPO" value="10 AM – 6 PM · Mon–Fri"/>
          </div>
        </div>
      </section>

      {/* Policy links */}
      <section className="mt-10 card-soft p-5">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-ink/60">More info</div>
        <div className="mt-3 flex flex-wrap gap-3 text-sm">
          <Link to="/terms" className="text-[#0D47A1] hover:underline">Terms & Conditions</Link>
          <span className="text-ink/30">·</span>
          <Link to="/privacy" className="text-[#0D47A1] hover:underline">Privacy Policy</Link>
          <span className="text-ink/30">·</span>
          <Link to="/refund-policy" className="text-[#0D47A1] hover:underline">Refund Policy</Link>
          <span className="text-ink/30">·</span>
          <Link to="/shipping-policy" className="text-[#0D47A1] hover:underline">Shipping Policy</Link>
        </div>
      </section>
    </div>
  );
}

function ContactCard({ testId, Icon, title, value, hint, tone = 'blue' }) {
  const styles = tone === 'green'
    ? 'bg-[#E8F5E9] border-[#A5D6A7] text-[#1B5E20]'
    : 'bg-[#E3F2FD] border-[#90CAF9] text-[#0D47A1]';
  return (
    <div data-testid={testId} className={`rounded-2xl border-2 p-5 ${styles}`}>
      <Icon className="w-6 h-6"/>
      <div className="mt-3 text-xs uppercase tracking-[0.18em] font-bold opacity-80">{title}</div>
      <div className="font-heading font-black text-lg mt-1 break-words">{value}</div>
      <div className="text-[11px] opacity-70 mt-1 leading-snug">{hint}</div>
    </div>
  );
}

function SpecCard({ Icon, title, email, phone, hint }) {
  return (
    <div className="card-soft p-5 flex items-start gap-3">
      <div className="w-11 h-11 rounded-xl bg-[#FFF3E0] text-[#9A4500] grid place-items-center flex-shrink-0"><Icon className="w-5 h-5"/></div>
      <div className="min-w-0">
        <div className="font-heading font-bold text-base text-ink">{title}</div>
        <div className="text-xs text-ink/60 mt-0.5">{hint}</div>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
          <a href={`mailto:${email}`} className="text-[#0D47A1] font-semibold hover:underline inline-flex items-center gap-1"><Mail className="w-3 h-3"/>{email}</a>
          <a href={`tel:${phone.replace(/\s+/g, '')}`} className="text-[#0D47A1] font-semibold hover:underline inline-flex items-center gap-1"><Phone className="w-3 h-3"/>{phone}</a>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-ink/60 text-xs uppercase tracking-wider">{label}</span>
      <span className="font-semibold text-ink text-right">{value}</span>
    </div>
  );
}

function CompanyIds() {
  const c = useCompany();
  return (
    <div data-testid="contact-company-ids">
      <div><b>{c?.legal_name || 'BharatCart'}</b></div>
      <div><b>GSTIN:</b> {c?.gstin || '—'} · <b>PAN:</b> {c?.pan || '—'}</div>
      {c?.address && <div>{c.address}</div>}
    </div>
  );
}
